import { useCallback, useEffect, useRef, useState } from "react";
import { Trophy, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAgent } from "@/context/AgentContext";
import { perfectDaySparks } from "@/lib/perfectDay";
import { speak } from "@/lib/tts";
import ShareButton from "@/components/share/ShareButton";

/**
 * PerfectDayCelebration — renders (1) cyan sparks via canvas-confetti,
 * (2) a translucent fullscreen overlay, and (3) a centered glass banner
 * announcing the 9/9 completion. Auto-dismisses after ~5s UNLESS the user
 * clicks "Compartir Día Perfecto" — in which case the dismiss timer is
 * cleared so the share preview / native-share sheet isn't killed by the
 * banner vanishing from under it.
 *
 * pointer-events: none on the outer overlay so the user can keep interacting
 * with the logros list / floating agent while the celebration plays. The X
 * button and the share CTA both opt-in to pointer events.
 */
const VISIBLE_MS = 5000;
const EXIT_MS = 500;

export default function PerfectDayCelebration() {
  const { perfectDay, clearPerfectDay } = useAgent();
  const { t, i18n } = useTranslation();
  const [phase, setPhase] = useState("idle"); // "idle" | "enter" | "exit"
  const pausedRef = useRef(false);
  const exitTimerRef = useRef(null);
  const clearTimerRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (exitTimerRef.current) { clearTimeout(exitTimerRef.current); exitTimerRef.current = null; }
    if (clearTimerRef.current) { clearTimeout(clearTimerRef.current); clearTimerRef.current = null; }
  }, []);

  useEffect(() => {
    if (!perfectDay) {
      setPhase("idle");
      return;
    }
    pausedRef.current = false;
    setPhase("enter");
    perfectDaySparks();
    // Phase 10: TTS uses the dynamic phrase "{N} de {N} logros" whenever
    // the trigger carried a total. Legacy trigger (no total) falls back to
    // the fixed 9/9 wording so old days celebrating in-flight don't break.
    const total = perfectDay.total;
    const ttsMsg =
      typeof total === "number" && total > 0
        ? t("celebration.perfect_day.tts_dynamic", { n: total })
        : t("celebration.perfect_day.tts");
    speak(ttsMsg, i18n.language || "es");

    exitTimerRef.current = setTimeout(() => {
      if (pausedRef.current) return;
      setPhase("exit");
    }, VISIBLE_MS);
    clearTimerRef.current = setTimeout(() => {
      if (pausedRef.current) return;
      clearPerfectDay();
      setPhase("idle");
    }, VISIBLE_MS + EXIT_MS);

    return () => { clearTimers(); };
  }, [perfectDay, clearPerfectDay, t, i18n.language, clearTimers]);

  const handleShareClick = useCallback(() => {
    // User wants to share — pause auto-dismiss so the preview modal can
    // live longer than the banner itself. The user can close the modal
    // later; the celebration stays put.
    pausedRef.current = true;
    clearTimers();
  }, [clearTimers]);

  const handleDismiss = useCallback(() => {
    clearTimers();
    setPhase("exit");
    setTimeout(() => { clearPerfectDay(); setPhase("idle"); }, EXIT_MS);
  }, [clearPerfectDay, clearTimers]);

  if (phase === "idle") return null;

  const totalForDisplay = perfectDay?.total;
  const hasDynamic =
    typeof totalForDisplay === "number" && totalForDisplay > 0;
  const titleText = hasDynamic
    ? t("celebration.perfect_day.title_dynamic", {
        n: totalForDisplay,
        defaultValue: `${totalForDisplay} / ${totalForDisplay}`,
      })
    : t("celebration.perfect_day.title");
  const subtitleText = hasDynamic
    ? t("celebration.perfect_day.subtitle_dynamic", { n: totalForDisplay })
    : t("celebration.perfect_day.subtitle");

  return (
    <div
      data-testid="perfect-day-overlay"
      data-phase={phase}
      className="nx-perfect-day"
      style={{ position: "fixed", inset: 0, zIndex: 9100, pointerEvents: "none" }}
      aria-live="polite"
      role="status"
    >
      {/* Translucent cyan wash — rapid fade in, slow out */}
      <div className="nx-perfect-day__wash" aria-hidden="true" />

      {/* Centered banner */}
      <div className="nx-perfect-day__banner-wrap">
        <div className="nx-perfect-day__banner" data-testid="perfect-day-banner">
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="perfect-day-dismiss"
            aria-label={t("common.close")}
            className="nx-perfect-day__close"
            style={{ pointerEvents: "auto" }}
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} />
          </button>

          <div className="nx-perfect-day__icon" aria-hidden="true">
            <Trophy className="h-8 w-8 text-[#00E5FF]" strokeWidth={1.75} />
          </div>

          <p className="nx-perfect-day__eyebrow">{t("celebration.perfect_day.eyebrow")}</p>
          <h2 className="nx-perfect-day__title" data-testid="perfect-day-title">{titleText}</h2>
          <p className="nx-perfect-day__subtitle" data-testid="perfect-day-subtitle">{subtitleText}</p>
          <p className="nx-perfect-day__tagline">{t("celebration.perfect_day.tagline")}</p>

          {/* Phase 9: share CTA — clicking pauses auto-dismiss and opens
              either the native share sheet or the preview modal. */}
          <div
            className="mt-4 flex justify-center"
            style={{ pointerEvents: "auto" }}
            onMouseDown={handleShareClick}
            onTouchStart={handleShareClick}
          >
            <ShareButton
              variant="primary"
              showLabel
              testId="perfect-day-share-button"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
